import { z } from 'zod';

import { ItemSchema, ItemCreateSchema, ItemUpdateSchema } from './item.js';

/**
 * Wire contract for the offline catalogue queue — `POST /items/sync`.
 *
 * The manager desktop app edits products and counts stock with no connection and
 * replays the queue on reconnect, so this endpoint has to answer three questions
 * the ordinary `/items` routes never face:
 *
 *   1. "Have I already applied this?"  Every op carries a device-generated
 *      `clientId`, claimed once in `item_sync_receipts` — a push whose response
 *      was lost replays to `deduped`, not a second product.
 *   2. "Is this edit still the newest?"  `editedAt` is when the operator actually
 *      typed it, which may be days before it arrives. An op older than the row it
 *      targets is `skipped_stale` and the current server item comes back so the
 *      device can settle without another round-trip.
 *   3. "Which product is this?"  A product created offline has no server id yet.
 *      Ops name it by `localId`, and the create earlier in the same batch teaches
 *      the server what that resolves to.
 *
 * The batch is applied in order and a bad op does NOT abort it: one duplicate
 * barcode among ninety-nine good edits must not strand the other ninety-nine on
 * the device forever. That is also why the one-of `itemId`/`localId` rule is
 * enforced in the service rather than by `.refine()` here — a schema-level
 * refinement fails the whole `parse`, taking the healthy ops down with it.
 *
 * Kept in its own file rather than growing `item.ts`, which describes what a
 * product IS; this describes how a disconnected device talks about changing one.
 */

// ---------------------------------------------------------------------------
// Ops
// ---------------------------------------------------------------------------

/** Fields every op shares: its idempotency key and when the human made the edit. */
const opBase = {
  /** Device-generated UUID, unique per queued op. The idempotency key. */
  clientId: z.string().uuid(),
  /** When the operator made this change on the device (ISO 8601). */
  editedAt: z.string().datetime(),
};

/**
 * A product invented offline. `localId` is the device's own row key; it comes
 * back on the result so the device can rewrite its local row to the server id.
 */
export const ItemSyncCreateOpSchema = z.object({
  ...opBase,
  op: z.literal('create'),
  localId: z.string().min(1).max(64),
  patch: ItemCreateSchema,
});
export type ItemSyncCreateOp = z.infer<typeof ItemSyncCreateOpSchema>;

/**
 * An edit to an existing product. `patch` carries ONLY the fields the operator
 * touched — sending a whole item back would clobber columns someone else changed
 * meanwhile, and `units`/`variants`/`barcodes` are full-replace, so an
 * accidentally-included empty array silently deletes them.
 *
 * Exactly one of `itemId` (already on the server) or `localId` (created earlier
 * in this same batch) must be present; the service, not the schema, enforces it.
 */
export const ItemSyncUpdateOpSchema = z.object({
  ...opBase,
  op: z.literal('update'),
  itemId: z.string().uuid().optional(),
  localId: z.string().min(1).max(64).optional(),
  patch: ItemUpdateSchema,
});
export type ItemSyncUpdateOp = z.infer<typeof ItemSyncUpdateOpSchema>;

/**
 * A shelf count. `qty` is the ABSOLUTE quantity the operator counted, not a
 * delta: a disconnected device cannot know the live level, and that ignorance is
 * the entire point of counting. The server works out the movement it implies.
 */
export const ItemSyncStockSetOpSchema = z.object({
  ...opBase,
  op: z.literal('stock_set'),
  itemId: z.string().uuid().optional(),
  localId: z.string().min(1).max(64).optional(),
  warehouseId: z.string().uuid(),
  qty: z.string().regex(/^-?\d+(\.\d{1,4})?$/),
  /** Needed only when the count is HIGHER than the books; falls back to the item's purchase price. */
  unitCost: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  unitCostCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).optional(),
  note: z.string().max(2000).optional(),
});
export type ItemSyncStockSetOp = z.infer<typeof ItemSyncStockSetOpSchema>;

export const ItemSyncOpSchema = z.discriminatedUnion('op', [
  ItemSyncCreateOpSchema,
  ItemSyncUpdateOpSchema,
  ItemSyncStockSetOpSchema,
]);
export type ItemSyncOp = z.infer<typeof ItemSyncOpSchema>;

/**
 * Cap matches the mandob sales push: big enough that a fortnight offline drains
 * in a few requests, small enough that the retry after a dropped connection is
 * cheap and the crash window between a write and its receipt stays short.
 */
export const ItemSyncBatchSchema = z.object({
  ops: z.array(ItemSyncOpSchema).min(1).max(500),
});
export type ItemSyncBatch = z.infer<typeof ItemSyncBatchSchema>;

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export const ItemSyncOpStatusSchema = z.enum([
  /** Written. For a create, `itemId` is the new product. */
  'applied',
  /** Already applied on an earlier push; `itemId` is what it produced then. */
  'deduped',
  /** Someone edited this product more recently; `item` carries the winner. */
  'skipped_stale',
  /** Refused. `reason` says why, and the device shows it to a human. */
  'failed',
]);
export type ItemSyncOpStatus = z.infer<typeof ItemSyncOpStatusSchema>;

/**
 * A closed set so the device can branch on it instead of matching prose. Every
 * one of these is a thing a person can act on — which is why `internal` is the
 * only vague member and the only one that should ever be rare.
 */
export const ItemSyncFailureReasonSchema = z.enum([
  'barcode_taken',
  'invalid_category_id',
  'invalid_brand_id',
  'not_found',
  /** Neither `itemId` nor `localId` was given, or both were. */
  'missing_item_ref',
  /** A `localId` no create in this batch (or any earlier push) accounted for. */
  'unresolved_local_id',
  /** The signed-in user may not do this KIND of op; the rest of the batch still ran. */
  'forbidden',
  /** The device clock is ahead of the server's by more than the tolerance. */
  'edited_at_in_future',
  /** Counting stock UP needs a cost and neither the op nor the item had one. */
  'unit_cost_required',
  'validation',
  'internal',
]);
export type ItemSyncFailureReason = z.infer<typeof ItemSyncFailureReasonSchema>;

/** Who already owns a barcode, so the device can name it rather than just refusing. */
export const ItemSyncBarcodeConflictSchema = z.object({
  barcode: z.string(),
  itemCode: z.string().nullable(),
  itemName: z.string().nullable(),
});
export type ItemSyncBarcodeConflict = z.infer<typeof ItemSyncBarcodeConflictSchema>;

export const ItemSyncOpResultSchema = z.object({
  clientId: z.string().uuid(),
  op: z.enum(['create', 'update', 'stock_set']),
  status: ItemSyncOpStatusSchema,
  /** Present on applied / deduped / skipped_stale. */
  itemId: z.string().uuid().optional(),
  /** Echoed on creates — the key the device maps to `itemId`. */
  localId: z.string().optional(),
  /** On skipped_stale: the timestamp that beat this op. */
  serverUpdatedAt: z.string().optional(),
  /**
   * On skipped_stale: the current server product, so the device overwrites its
   * losing copy immediately instead of waiting for the next catalogue pull.
   */
  item: ItemSchema.optional(),
  reason: ItemSyncFailureReasonSchema.optional(),
  message: z.string().optional(),
  conflict: ItemSyncBarcodeConflictSchema.optional(),
});
export type ItemSyncOpResult = z.infer<typeof ItemSyncOpResultSchema>;

export const ItemSyncResponseSchema = z.object({
  results: z.array(ItemSyncOpResultSchema),
});
export type ItemSyncResponse = z.infer<typeof ItemSyncResponseSchema>;

/**
 * How far ahead of the server a device's clock may be before its edits are
 * refused. A PC with a wrong clock is common; one whose `editedAt` lands in the
 * future is not merely wrong but un-overwritable, because every later real edit
 * would lose the newest-wins comparison to it. Within tolerance the server
 * clamps to now; beyond it the op fails so a human fixes the clock.
 */
export const ITEM_SYNC_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;
